module Lexeme
  ( Lexeme(..)
  , generator
  , parser
  , print
  , printMany
  , checkParsePrint
  , render
  , renderMany
  ) where

import Prelude hiding (div)

import Control.Monad.Gen (class MonadGen)
import Control.Monad.Gen as Gen
import Control.Monad.Rec.Class (class MonadRec)
import Data.Array (fromFoldable, some)
import Data.Array.NonEmpty (cons')
import Data.CodePoint.Unicode (isLetter)
import Data.Either (either)
import Data.Foldable (class Foldable, foldr)
import Data.Foldable as Foldable
import Data.Generic.Rep (class Generic)
import Data.Show.Generic (genericShow)
import Data.String (codePointFromChar)
import Data.String.CodeUnits (fromCharArray, singleton)
import Data.String.Gen (genAlphaString)
import Data.Tuple (Tuple(..), snd, uncurry)
import Halogen (ClassName(..))
import Halogen.HTML (HTML, div, span, text)
import Halogen.HTML.Properties (class_, classes)
import Parsing (Parser, fail, runParser)
import Parsing.Combinators (try)
import Parsing.String (anyChar, anyCodePoint, char, satisfy)

data Lexeme
  = Lambda
  | Identifier String
  | Dot
  | OpenParen
  | CloseParen

derive instance eqLexeme :: Eq Lexeme
derive instance genericLexeme :: Generic Lexeme _

instance Show Lexeme where
  show = genericShow

generator :: forall g. MonadRec g => MonadGen g => g Lexeme
generator =
  Gen.oneOf
    $ cons' (Identifier <$> genAlphaString)
    $ map pure [ Lambda, Dot, OpenParen, CloseParen ]

identifier :: Parser String String
identifier =
  try $ fromCharArray <$> some (satisfy $ isLetter <<< codePointFromChar)

parser :: Parser String Lexeme
parser =
  Foldable.oneOf
    [ Lambda <$ char '\\'
    , Dot <$ char '.'
    , OpenParen <$ char '('
    , CloseParen <$ char ')'
    , Identifier <$> identifier
    , do
        badChar <- anyChar
        fail $ "Bad input character: " <> show badChar
    , do
        badCode <- anyCodePoint
        fail $ "Bad input code point (!): " <> show badCode
    ]

-- | Not necessarily pretty, but human-consumable instead of expression-like; semi-inverse of 'lexeme'.
print :: Lexeme -> String
print Lambda = singleton '\\'
print (Identifier s) = s
print Dot = singleton '.'
print OpenParen = singleton '('
print CloseParen = singleton ')'

-- | Like `foldMap print` but with required whitespace (separating adjacent identifiers) added.
printMany :: forall f. Foldable f => f Lexeme -> String
printMany ls = snd $ foldr (uncurry <<< alg) (Tuple false "") ls
  where
  alg l leadingId t = case l of
    Identifier x -> Tuple true ((if leadingId then (x <> " ") else x) <> t)
    _ -> Tuple false $ print l <> t

-- | QuickCheck property; semantically: parse . print = id
checkParsePrint :: Lexeme -> Boolean
checkParsePrint x =
  either (const false) (x == _) $ print x `runParser` parser

render :: forall slot action. Lexeme -> HTML slot action
render lex = span
  [ classes $ [ cLexeme "display", cType ty ] ]
  [ text txt ]
  where
  Tuple ty txt =
    case lex of
      Lambda -> Tuple "lambda" "\\"
      Identifier id -> Tuple "identifier" id
      Dot -> Tuple "dot" "."
      OpenParen -> Tuple "openParen" "("
      CloseParen -> Tuple "closeParen" ")"
  cLexeme c = ClassName $ "lexeme_" <> c
  cType c = cLexeme $ "tag_" <> c

renderMany ::
  forall f slot action. Functor f => Foldable f => f Lexeme -> HTML slot action
renderMany ls =
  div
    [ class_ $ ClassName "lexemes_display" ]
    (fromFoldable (render <$> ls))
